import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Progress,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tag,
  Text,
  Textarea,
  VStack,
  Avatar,
  Badge,
  useColorModeValue,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Link,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import {
  FiArrowUp,
  FiArrowDown,
  FiCheck,
  FiX,
  FiMessageCircle,
  FiClock,
  FiZap,
  FiDollarSign,
  FiStar,
  FiActivity,
  FiSettings,
  FiFileText,
  FiThumbsUp,
  FiThumbsDown,
  FiSend,
  FiInfo,
  FiExternalLink,
  FiBarChart2,
  FiUsers,
  FiAlertCircle,
} from 'react-icons/fi';
import { governanceAPI } from '../utils/api';

const ProposalDetail = ({ proposal, onClose }) => {
  const { connected, publicKey } = useWallet();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [votingHistory, setVotingHistory] = useState([]);
  const [analysisData, setAnalysisData] = useState(null);
  
  // Theme colors
  const cardBg = useColorModeValue('white', 'dark.card');
  const borderColor = useColorModeValue('gray.200', 'dark.border');
  const textColor = useColorModeValue('gray.800', 'dark.textPrimary');
  const secondaryTextColor = useColorModeValue('gray.600', 'dark.textSecondary');
  const statBg = useColorModeValue('blue.50', 'rgba(118, 120, 237, 0.1)');
  
  // Mock voting history data
  const mockVotingHistory = [
    {
      voter: 'DYw8jMTrFWji7h4VTSWQcKxmwBQkYxgCHvKiUn7MTcgV',
      vote: 'yes',
      timestamp: '2023-09-12T15:30:00Z',
      weight: 1,
    },
    {
      voter: 'EXU6iBX8qvdYu9bLQUWiKRmQVQ5nSELHqWfJSNVEjXxz',
      vote: 'yes',
      timestamp: '2023-09-12T15:35:00Z',
      weight: 1,
    },
    {
      voter: 'FZLEuNjqJFdtSfS9AJAg3nGJNxpLMi3XAKmwYDHHGN9B',
      vote: 'no',
      timestamp: '2023-09-12T15:40:00Z',
      weight: 1,
    },
    {
      voter: 'GpYnVDj7jWsJUbzii8PfabK3PgJz1VZwLs5i4XVnYu3S',
      vote: 'yes',
      timestamp: '2023-09-12T15:45:00Z',
      weight: 1,
    },
    {
      voter: 'HqVxzRWf6RZVhksVLgj9SYQCytGEzVrh8NDADJ4F5X1T',
      vote: 'yes',
      timestamp: '2023-09-12T15:50:00Z',
      weight: 1,
    }
  ];
  
  // Mock comments data
  const mockComments = [
    {
      id: 1,
      author: 'DYw8jMTrFWji7h4VTSWQcKxmwBQkYxgCHvKiUn7MTcgV',
      authorName: 'SolTrader',
      content: 'I think this is a good opportunity to increase our exposure to memecoins. The market sentiment is positive right now.',
      timestamp: '2023-09-12T16:00:00Z',
      likes: 5,
      dislikes: 1,
    },
    {
      id: 2,
      author: 'EXU6iBX8qvdYu9bLQUWiKRmQVQ5nSELHqWfJSNVEjXxz',
      authorName: 'DegenInvestor',
      content: 'Have we considered the liquidity risks? The slippage might be high for this token.',
      timestamp: '2023-09-12T16:15:00Z',
      likes: 3,
      dislikes: 0,
    },
    {
      id: 3,
      author: 'FZLEuNjqJFdtSfS9AJAg3nGJNxpLMi3XAKmwYDHHGN9B',
      authorName: 'CryptoWhale',
      content: 'I disagree with this proposal. The token has no utility and is purely speculative.',
      timestamp: '2023-09-12T16:30:00Z',
      likes: 2,
      dislikes: 4,
    },
    {
      id: 4,
      author: 'GpYnVDj7jWsJUbzii8PfabK3PgJz1VZwLs5i4XVnYu3S',
      authorName: 'SolanaBuilder',
      content: 'Looking at the technical analysis, this seems like a good entry point. Support levels are strong.',
      timestamp: '2023-09-12T16:45:00Z',
      likes: 7,
      dislikes: 1,
    }
  ];
  
  // Mock analysis data
  const mockAnalysisData = {
    marketCap: '$15.2M',
    volume24h: '$3.5M',
    priceChange24h: '+12.5%',
    priceChange7d: '+45.2%',
    liquidityDepth: '$1.2M',
    holders: 12500,
    sentiment: {
      positive: 65,
      neutral: 25,
      negative: 10
    },
    riskScore: 7.8,
    technicalIndicators: {
      macd: 'bullish',
      rsi: 68,
      movingAverages: 'buy'
    },
    communityMetrics: {
      twitter: 25000,
      telegram: 15000,
      discord: 8000
    },
    sources: [
      'CoinGecko',
      'DexScreener',
      'Solscan',
      'Twitter API'
    ]
  };
  
  useEffect(() => {
    // In a real app, fetch comments and voting history from API
    setComments(mockComments);
    setVotingHistory(mockVotingHistory);
    setAnalysisData(mockAnalysisData);
  }, [proposal]);
  
  const handleVote = async (vote) => {
    if (!connected) {
      alert('Please connect your wallet to vote');
      return;
    }
    
    try {
      // In a real app, send vote to API
      await governanceAPI.voteOnProposal(proposal.id, vote, publicKey?.toString());
      
      // Optimistic UI update
      if (vote === 'yes') {
        proposal.votes.yes += 1;
        proposal.votes.total += 1;
      } else {
        proposal.votes.no += 1;
        proposal.votes.total += 1;
      }
      
      // Add to voting history
      setVotingHistory([
        {
          voter: publicKey?.toString(),
          vote: vote,
          timestamp: new Date().toISOString(),
          weight: 1,
        },
        ...votingHistory
      ]);
      
    } catch (error) {
      console.error('Error voting:', error);
      alert('Error submitting your vote. Please try again.');
    }
  };
  
  const handleCommentSubmit = async () => {
    if (!connected) {
      alert('Please connect your wallet to comment');
      return;
    }
    
    if (!newComment.trim()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // In a real app, send comment to API
      // const response = await governanceAPI.addComment(proposal.id, newComment, publicKey?.toString());
      
      // Optimistic UI update
      const newCommentObj = {
        id: comments.length + 1,
        author: publicKey?.toString(),
        authorName: 'You',
        content: newComment,
        timestamp: new Date().toISOString(),
        likes: 0,
        dislikes: 0,
      };
      
      setComments([newCommentObj, ...comments]);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error submitting your comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleCommentReaction = (commentId, reaction) => {
    setComments(comments.map(comment => {
      if (comment.id === commentId) {
        if (reaction === 'like') {
          return { ...comment, likes: comment.likes + 1 };
        } else {
          return { ...comment, dislikes: comment.dislikes + 1 };
        }
      }
      return comment;
    }));
  };
  
  // Get badge color based on proposal type
  const getProposalTypeColor = (type) => {
    switch (type) {
      case 'trade':
        return 'green';
      case 'whitelist':
        return 'purple';
      case 'parameter':
        return 'blue';
      case 'strategy':
        return 'orange';
      default:
        return 'gray';
    }
  };
  
  // Get icon based on proposal type
  const getProposalTypeIcon = (type) => {
    switch (type) {
      case 'trade':
        return FiDollarSign;
      case 'whitelist':
        return FiStar;
      case 'parameter':
        return FiSettings;
      case 'strategy':
        return FiActivity;
      default:
        return FiFileText;
    }
  };
  
  const isActive = proposal.status === 'active';
  const yesPercentage = proposal.votes.yes;
  const noPercentage = proposal.votes.no;
  
  return (
    <Box>
      <Card 
        bg={cardBg}
        borderColor={borderColor}
        borderWidth="1px"
        borderRadius="lg"
        overflow="hidden"
        mb={4}
      >
        <CardHeader pb={2}>
          <Flex justify="space-between" align="flex-start">
            <Box>
              <Flex align="center" mb={2}>
                <Tag 
                  size="sm" 
                  colorScheme={getProposalTypeColor(proposal.type)}
                  borderRadius="full"
                  mr={2}
                >
                  <Icon as={getProposalTypeIcon(proposal.type)} mr={1} />
                  {proposal.type.charAt(0).toUpperCase() + proposal.type.slice(1)}
                </Tag>
                {proposal.tradeType === 'flash' && (
                  <Tag size="sm" colorScheme="yellow" borderRadius="full">
                    <Icon as={FiZap} mr={1} />
                    Fast Trade
                  </Tag>
                )}
              </Flex>
              <Heading size="md" color={textColor}>{proposal.title}</Heading>
              <Text fontSize="sm" color={secondaryTextColor} mt={1}>
                {proposal.description}
              </Text>
            </Box>
            <Flex direction="column" align="flex-end">
              <Text 
                fontSize="sm" 
                fontWeight="medium"
                color={isActive ? 'green.500' : 'gray.500'}
              >
                {isActive ? (
                  <Flex align="center">
                    <Icon as={FiClock} mr={1} />
                    {proposal.timeLeft} left
                  </Flex>
                ) : (
                  proposal.status
                )}
              </Text>
            </Flex>
          </Flex>
        </CardHeader>
        
        <CardBody pt={0}>
          {/* Proposal Details */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
            <Box>
              <Text fontWeight="bold" mb={2}>Proposal Details</Text>
              <VStack align="start" spacing={2}>
                <Flex>
                  <Text fontWeight="medium" width="120px">Proposer:</Text>
                  <Text>{proposal.proposer}</Text>
                </Flex>
                {proposal.token && (
                  <Flex>
                    <Text fontWeight="medium" width="120px">Token:</Text>
                    <Text>{proposal.token}</Text>
                  </Flex>
                )}
                {proposal.action && (
                  <Flex>
                    <Text fontWeight="medium" width="120px">Action:</Text>
                    <Text>{proposal.action}</Text>
                  </Flex>
                )}
                {proposal.amount && (
                  <Flex>
                    <Text fontWeight="medium" width="120px">Amount:</Text>
                    <Text>{proposal.amount}</Text>
                  </Flex>
                )}
                {proposal.targetPrice && (
                  <Flex>
                    <Text fontWeight="medium" width="120px">Target Price:</Text>
                    <Text>{proposal.targetPrice}</Text>
                  </Flex>
                )}
                <Flex>
                  <Text fontWeight="medium" width="120px">Created:</Text>
                  <Text>{new Date(proposal.createdAt).toLocaleString()}</Text>
                </Flex>
                <Flex>
                  <Text fontWeight="medium" width="120px">Expires:</Text>
                  <Text>{new Date(proposal.expiresAt).toLocaleString()}</Text>
                </Flex>
              </VStack>
            </Box>
            
            <Box>
              <Text fontWeight="bold" mb={2}>Voting Status</Text>
              <Box mt={2}>
                <Flex justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="medium">Yes ({yesPercentage}%)</Text>
                  <Text fontSize="sm" fontWeight="medium">No ({noPercentage}%)</Text>
                </Flex>
                <Flex>
                  <Progress 
                    value={yesPercentage} 
                    colorScheme="green" 
                    height="8px" 
                    borderLeftRadius="full"
                    borderRightRadius="none"
                    flex={yesPercentage}
                    min={0}
                  />
                  <Progress 
                    value={noPercentage} 
                    colorScheme="red" 
                    height="8px" 
                    borderLeftRadius="none"
                    borderRightRadius="full"
                    flex={noPercentage}
                    min={0}
                  />
                </Flex>
                <Text fontSize="xs" color={secondaryTextColor} mt={1}>
                  {proposal.votes.total} votes total
                </Text>
              </Box>
              
              {isActive && (
                <HStack mt={4} spacing={2}>
                  <Button 
                    colorScheme="green" 
                    leftIcon={<FiCheck />}
                    onClick={() => handleVote('yes')}
                    isDisabled={!connected}
                    size="sm"
                    flex={1}
                  >
                    Vote Yes
                  </Button>
                  <Button 
                    colorScheme="red" 
                    leftIcon={<FiX />}
                    onClick={() => handleVote('no')}
                    isDisabled={!connected}
                    size="sm"
                    flex={1}
                  >
                    Vote No
                  </Button>
                </HStack>
              )}
              
              {proposal.tradeType === 'flash' && isActive && (
                <Button
                  colorScheme="yellow"
                  leftIcon={<FiZap />}
                  mt={2}
                  size="sm"
                  width="100%"
                  isDisabled={!connected}
                >
                  Fast Track Approval
                </Button>
              )}
            </Box>
          </SimpleGrid>
          
          {/* Tabs for Discussion, Voting History, and Analysis */}
          <Tabs colorScheme="brand" mt={6} onChange={(index) => setActiveTab(index)}>
            <TabList>
              <Tab><Icon as={FiMessageCircle} mr={2} /> Discussion ({comments.length})</Tab>
              <Tab><Icon as={FiUsers} mr={2} /> Voting History</Tab>
              <Tab><Icon as={FiBarChart2} mr={2} /> Analysis</Tab>
            </TabList>
            
            <TabPanels>
              {/* Discussion Tab */}
              <TabPanel px={0}>
                {connected ? (
                  <Box mb={4}>
                    <Textarea
                      placeholder="Add your comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      mb={2}
                    />
                    <Button
                      colorScheme="brand"
                      leftIcon={<FiSend />}
                      onClick={handleCommentSubmit}
                      isLoading={isSubmitting}
                      isDisabled={!newComment.trim()}
                      size="sm"
                    >
                      Post Comment
                    </Button>
                  </Box>
                ) : (
                  <Alert status="info" mb={4}>
                    <AlertIcon />
                    <AlertTitle>Connect wallet</AlertTitle>
                    <AlertDescription>Connect your wallet to join the discussion</AlertDescription>
                  </Alert>
                )}
                
                <VStack spacing={4} align="stretch">
                  {comments.map((comment) => (
                    <Card key={comment.id} size="sm" variant="outline">
                      <CardBody>
                        <Flex justify="space-between" mb={2}>
                          <HStack>
                            <Avatar size="xs" name={comment.authorName} />
                            <Text fontWeight="bold">{comment.authorName}</Text>
                            <Text fontSize="xs" color={secondaryTextColor}>
                              {new Date(comment.timestamp).toLocaleString()}
                            </Text>
                          </HStack>
                        </Flex>
                        <Text>{comment.content}</Text>
                        <HStack mt={2} spacing={4}>
                          <Button
                            size="xs"
                            leftIcon={<FiThumbsUp />}
                            variant="ghost"
                            onClick={() => handleCommentReaction(comment.id, 'like')}
                          >
                            {comment.likes}
                          </Button>
                          <Button
                            size="xs"
                            leftIcon={<FiThumbsDown />}
                            variant="ghost"
                            onClick={() => handleCommentReaction(comment.id, 'dislike')}
                          >
                            {comment.dislikes}
                          </Button>
                        </HStack>
                      </CardBody>
                    </Card>
                  ))}
                </VStack>
              </TabPanel>
              
              {/* Voting History Tab */}
              <TabPanel px={0}>
                <VStack spacing={2} align="stretch">
                  {votingHistory.map((vote, index) => (
                    <Flex 
                      key={index} 
                      justify="space-between" 
                      p={2} 
                      borderBottom="1px solid" 
                      borderColor={borderColor}
                    >
                      <HStack>
                        <Avatar size="xs" name={vote.voter} />
                        <Text fontSize="sm">{vote.voter.substring(0, 6)}...{vote.voter.substring(vote.voter.length - 4)}</Text>
                      </HStack>
                      <HStack>
                        <Badge colorScheme={vote.vote === 'yes' ? 'green' : 'red'}>
                          {vote.vote === 'yes' ? 'Yes' : 'No'}
                        </Badge>
                        <Text fontSize="xs" color={secondaryTextColor}>
                          {new Date(vote.timestamp).toLocaleString()}
                        </Text>
                      </HStack>
                    </Flex>
                  ))}
                </VStack>
              </TabPanel>
              
              {/* Analysis Tab */}
              <TabPanel px={0}>
                {analysisData && (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                      <Text fontWeight="bold" mb={2}>Market Data</Text>
                      <VStack align="start" spacing={2}>
                        <Flex>
                          <Text fontWeight="medium" width="150px">Market Cap:</Text>
                          <Text>{analysisData.marketCap}</Text>
                        </Flex>
                        <Flex>
                          <Text fontWeight="medium" width="150px">24h Volume:</Text>
                          <Text>{analysisData.volume24h}</Text>
                        </Flex>
                        <Flex>
                          <Text fontWeight="medium" width="150px">24h Change:</Text>
                          <Text color={analysisData.priceChange24h.startsWith('+') ? 'green.500' : 'red.500'}>
                            {analysisData.priceChange24h}
                          </Text>
                        </Flex>
                        <Flex>
                          <Text fontWeight="medium" width="150px">7d Change:</Text>
                          <Text color={analysisData.priceChange7d.startsWith('+') ? 'green.500' : 'red.500'}>
                            {analysisData.priceChange7d}
                          </Text>
                        </Flex>
                        <Flex>
                          <Text fontWeight="medium" width="150px">Liquidity Depth:</Text>
                          <Text>{analysisData.liquidityDepth}</Text>
                        </Flex>
                        <Flex>
                          <Text fontWeight="medium" width="150px">Holders:</Text>
                          <Text>{analysisData.holders.toLocaleString()}</Text>
                        </Flex>
                      </VStack>
                    </Box>
                    
                    <Box>
                      <Text fontWeight="bold" mb={2}>Technical Analysis</Text>
                      <VStack align="start" spacing={2}>
                        <Flex>
                          <Text fontWeight="medium" width="150px">MACD:</Text>
                          <Text color={analysisData.technicalIndicators.macd === 'bullish' ? 'green.500' : 'red.500'}>
                            {analysisData.technicalIndicators.macd}
                          </Text>
                        </Flex>
                        <Flex>
                          <Text fontWeight="medium" width="150px">RSI:</Text>
                          <Text>{analysisData.technicalIndicators.rsi}</Text>
                        </Flex>
                        <Flex>
                          <Text fontWeight="medium" width="150px">Moving Averages:</Text>
                          <Text>{analysisData.technicalIndicators.movingAverages}</Text>
                        </Flex>
                        <Flex>
                          <Text fontWeight="medium" width="150px">Risk Score:</Text>
                          <Text color={analysisData.riskScore > 7 ? 'red.500' : analysisData.riskScore > 4 ? 'yellow.500' : 'green.500'}>
                            {analysisData.riskScore}/10
                          </Text>
                        </Flex>
                      </VStack>
                      
                      <Text fontWeight="bold" mt={4} mb={2}>Community Metrics</Text>
                      <VStack align="start" spacing={2}>
                        <Flex>
                          <Text fontWeight="medium" width="150px">Twitter Followers:</Text>
                          <Text>{analysisData.communityMetrics.twitter.toLocaleString()}</Text>
                        </Flex>
                        <Flex>
                          <Text fontWeight="medium" width="150px">Telegram Members:</Text>
                          <Text>{analysisData.communityMetrics.telegram.toLocaleString()}</Text>
                        </Flex>
                        <Flex>
                          <Text fontWeight="medium" width="150px">Discord Members:</Text>
                          <Text>{analysisData.communityMetrics.discord.toLocaleString()}</Text>
                        </Flex>
                      </VStack>
                    </Box>
                    
                    <Box gridColumn={{ md: "span 2" }}>
                      <Text fontSize="xs" color={secondaryTextColor} mt={2}>
                        Data sources: {analysisData.sources.join(', ')}
                      </Text>
                    </Box>
                  </SimpleGrid>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
        
        <Divider borderColor={borderColor} />
        
        <CardFooter pt={3} pb={3}>
          <Flex width="100%" justify="space-between" align="center">
            <Button 
              size="sm" 
              variant="outline"
              onClick={onClose}
            >
              Back to Proposals
            </Button>
            
            {isActive && proposal.type === 'trade' && (
              <HStack>
                <Button 
                  size="sm" 
                  colorScheme={proposal.tradeType === 'flash' ? "yellow" : "green"}
                  leftIcon={proposal.tradeType === 'flash' ? <FiZap /> : <FiDollarSign />}
                  isDisabled={!connected}
                >
                  Fast Trade
                </Button>
              </HStack>
            )}
          </Flex>
        </CardFooter>
      </Card>
    </Box>
  );
};

export default ProposalDetail;